import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart, ExternalLink, Loader2, Mic2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SoundtracksPage() {
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${API}/albums`);
        if (!cancelled) {
          setAllItems(response.data);
          setError(false);
        }
      } catch (err) {
        console.error('Error fetching soundtracks:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchItems();
    return () => { cancelled = true; };
  }, []);

  const soundtracks = allItems.filter(item => item.item_type === 'soundtrack');

  const handleAdd = (item) => {
    addItem(item, 'digital', 1);
    toast.success(
      t(`Added to cart: ${item.title_es || item.title}`, `Añadido al carrito: ${item.title_es || item.title}`)
    );
  };

  const MusicCard = ({ item }) => (
    <Card
      className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white"
      data-testid={`music-item-${item.id}`}
    >
      <Link to={`/music/${item.id}`} className="block">
        <div className="relative aspect-square overflow-hidden group">
          <img
            src={item.image_url}
            alt={language === 'en' ? item.title : item.title_es}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/40 transition-colors duration-300 flex items-center justify-center">
            <span className="font-lato text-white text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gold-500 px-4 py-2 rounded-full">
              {t('View Details', 'Ver Detalles')}
            </span>
          </div>
          <span className="absolute top-4 left-4 bg-charcoal text-white font-lato text-xs uppercase tracking-wider px-3 py-1">
            {t('Soundtrack', 'Pista')}
          </span>
        </div>
      </Link>
      <CardContent className="p-6">
        <Link to={`/music/${item.id}`} className="block hover:text-gold-600 transition-colors">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-playfair text-xl font-bold text-charcoal hover:text-gold-600 transition-colors">
                {language === 'en' ? item.title : item.title_es}
              </h3>
              <p className="font-lato text-gray-500 text-sm">{item.year}</p>
            </div>
            <span className="font-playfair text-2xl font-bold text-gold-600">
              ${item.price.toFixed(2)}
            </span>
          </div>
        </Link>
        <p className="font-lato text-gray-600 text-sm mb-4 line-clamp-2">
          {language === 'en' ? item.description : item.description_es}
        </p>
        <div className="flex gap-3">
          <Button
            onClick={(e) => { e.preventDefault(); handleAdd(item); }}
            className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-lato uppercase tracking-wider"
            data-testid={`buy-${item.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('Add to Cart', 'Añadir al Carrito')}
          </Button>
          {item.spotify_url && (
            <a href={item.spotify_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-gold-300 text-gold-600 hover:bg-gold-50">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="soundtracks-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-charcoal">
        <div className="container mx-auto px-4 md:px-8">
          <p className="font-lato text-gold-400 text-sm uppercase tracking-[0.3em] mb-2">
            {t('Sarah Capeles', 'Sarah Capeles')}
          </p>
          <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-4">
            {t('Soundtracks', 'Pistas')}
          </h1>
          <p className="font-lato text-xl text-gray-400 max-w-2xl">
            {t(
              'Instrumental backing tracks and karaoke versions. Perfect for worship leaders, choirs, and soloists.',
              'Pistas instrumentales y versiones karaoke. Perfectas para líderes de adoración, coros y solistas.'
            )}
          </p>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="font-lato text-gray-500 text-lg">
            {t('Failed to load soundtracks', 'Error al cargar las pistas')}
          </p>
          <Button
            onClick={() => { setLoading(true); setError(false); window.location.reload(); }}
            className="bg-gold-500 hover:bg-gold-400 text-white font-lato"
            data-testid="retry-button"
          >
            {t('Try Again', 'Intentar de Nuevo')}
          </Button>
        </div>
      ) : (
        <section className="py-16" data-testid="soundtracks-section">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center gap-4 mb-8">
              <Mic2 className="w-8 h-8 text-gold-500" />
              <h2 className="font-playfair text-3xl font-bold text-charcoal">
                {t('All Soundtracks', 'Todas las Pistas')} <span className="text-gold-500">({soundtracks.length})</span>
              </h2>
            </div>
            {soundtracks.length === 0 ? (
              <div className="py-16 text-center">
                <Mic2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="font-lato text-gray-500 text-lg mb-2">
                  {t('No soundtracks available yet.', 'No hay pistas disponibles aún.')}
                </p>
                <p className="font-lato text-gray-400 text-sm">
                  {t('Check back soon — new instrumental tracks coming!', '¡Vuelve pronto — nuevas pistas instrumentales en camino!')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {soundtracks.map((item) => (
                  <MusicCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Disclaimer (bottom of page) */}
      <section className="bg-cream border-t border-gold-200" data-testid="soundtracks-disclaimer">
        <div className="container mx-auto px-4 md:px-8 py-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-playfair text-xl font-bold text-charcoal uppercase tracking-wider mb-4">
              {t('Disclaimer', 'Descargo de Responsabilidad')}
            </h2>
            <div className="font-lato text-sm text-gray-700 leading-relaxed space-y-3">
              <p>
                {t(
                  'All purchased music tracks are strictly licensed for live performance use only. It is strictly forbidden to record, reproduce, distribute, transmit, upload to digital platforms, sell, or use these music tracks for any type of audio or video recording without prior written authorization.',
                  'Todas las pistas musicales adquiridas están autorizadas estrictamente para uso en presentaciones en vivo únicamente. Está terminantemente prohibido grabar, reproducir, distribuir, transmitir, subir a plataformas digitales, vender o utilizar estas pistas musicales para cualquier tipo de grabación de audio o video sin autorización previa por escrito.'
                )}
              </p>
              <p>
                {t(
                  'Any unauthorized recording or use of these tracks will constitute a violation of this agreement and may result in a penalty of $500 per infringement, in addition to any other applicable legal action.',
                  'Cualquier grabación o uso no autorizado de estas pistas constituirá una violación de este acuerdo y podrá resultar en una penalidad de $500 por cada infracción, además de cualquier otra acción legal aplicable.'
                )}
              </p>
              <p className="font-semibold text-charcoal">
                {t(
                  'By purchasing and/or using these music tracks, the buyer acknowledges and accepts these terms.',
                  'Al comprar y/o utilizar estas pistas musicales, el comprador reconoce y acepta estos términos.'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
